export type FillDirection = 'below' | 'above' | 'none';

export type DivergingFill = {
  /** Left edge of the fill, as a percentage of the track width. */
  left: number;
  /** Width of the fill, as a percentage of the track width. */
  width: number;
  /** Value position, as a percentage of the track width. */
  valuePosition: number;
  /** Baseline anchor position, as a percentage of the track width. */
  baselinePosition: number;
  /** Which side of the baseline the value falls on. */
  direction: FillDirection;
};

export type ComputeDivergingFillInput = {
  value: number;
  min: number;
  max: number;
  baseline: number;
};

const clamp = (n: number, lower: number, upper: number) =>
  Math.max(lower, Math.min(upper, n));

/**
 * Pure geometry for a diverging fill bar: a fill that grows from a baseline
 * anchor towards the value, with length proportional to their distance on the
 * [min, max] scale. `direction` reports which side of the baseline the value
 * falls on; the fill colour is the caller's concern.
 */
export function computeDivergingFill({
  value,
  min,
  max,
  baseline,
}: ComputeDivergingFillInput): DivergingFill {
  const span = max - min;
  const toPercent = (n: number) =>
    span > 0 ? clamp(((n - min) / span) * 100, 0, 100) : 0;

  const baselinePosition = toPercent(baseline);
  const valuePosition = toPercent(value);
  const direction: FillDirection =
    value < baseline ? 'below' : value > baseline ? 'above' : 'none';

  return {
    left: Math.min(baselinePosition, valuePosition),
    width: Math.abs(valuePosition - baselinePosition),
    valuePosition,
    baselinePosition,
    direction,
  };
}
